'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { StripePrice, useCreateFinancialConnectionSessionMutation, useGetStripePricesQuery, useGetTaxLazyQuery } from 'graphql/generated/graphql'
import { useFormDataContext, FormDataProvider } from 'hook/useFormData'
import Image from 'next/image'
import { ArrowLeftIcon } from '@phosphor-icons/react'
import { Bank, Card } from 'iconsax-react'
import { Elements } from '@stripe/react-stripe-js'
import { getStripe } from '@/lib/stripe'
import StripeCheckoutForm from '../../app/(modalScreens)/account-upgrade/StripeCheckOutForm'
import { CouponDetailType, FinConnectSession, PaymentStateType } from 'types/common'
import SubscriptionPlanCard from '@/components/modals/subscription-plan-card'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { X } from 'lucide-react'
import Link from 'next/link'
import { dateAfterMonths } from '@/lib/utils'


interface TaxInput {
  taxAmount: number
  totalAmount: number
  taxPercentage?: number
}

interface UpgradePaymentModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (isTaxExempt: boolean) => void
  userData: {
    firstName: string
    lastName: string
    email: string
    phone: string
    address: string
    city: string
    state: string
    zip: string
  }
}

export function UpgradePaymentModal({
  open,
  onClose,
  onSuccess,
  userData,
}: UpgradePaymentModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<StripePrice | null>(null)
  const [checkoutType, setCheckoutType] = useState<string>('card')
  const [taxDetail, setTaxDetails] = useState<TaxInput>({
    taxAmount: 0,
    totalAmount: 0,
    taxPercentage: 0,
  })
  const [couponDetail, setCouponDetail] = useState<CouponDetailType | null>(null)
  const [taxExemption, setTaxExemption] = useState<boolean>(false)
  const [finConnSession, setFinConnSession] = useState<FinConnectSession>()
  const [paymentState, setPaymentState] = useState<PaymentStateType>({
    success: false,
    failed: false,
    paymentType: false,
  })

  // formData context
  const { setFormData, formData } = useFormDataContext()
  const { data, loading: pricesLoading, error: pricesError } = useGetStripePricesQuery({ fetchPolicy: 'no-cache' })
  const [getTaxDetails] = useGetTaxLazyQuery({ fetchPolicy: 'no-cache' })
  const [getFinancialConnectionSession] = useCreateFinancialConnectionSessionMutation({ fetchPolicy: 'no-cache' })

  // Pre-populate formData with user data once on open
  useEffect(() => {
    if (userData) {
      setFormData((prev) => ({
        ...prev,
        firstName: userData.firstName,
        lastName: userData.lastName,
        contactEmail: userData.email,
        contactPhone: userData.phone,
        streetAddress: userData.address,
        city: userData.city,
        state: userData.state,
        zip: userData.zip,
      }))
    }
  }, [userData, setFormData])

  // Set initial plan to annual
  useEffect(() => {
    if (data?.stripePrices && data.stripePrices.length > 0 && !selectedPlan) {
      const annualPlan = data.stripePrices.find(
        (p) => p.interval?.toLowerCase() === 'year' && p.active
      )
      if (annualPlan) {
        setSelectedPlan(annualPlan)
      }
    }
  }, [data, selectedPlan])

  // Compute discount + base amount consistently
  const computeDiscount = useMemo(() => {
    if (!selectedPlan || !couponDetail?.discount) return 0

    const unitAmount = selectedPlan.unitAmount
    if (couponDetail.discount.includes('%')) {
      const percent = parseFloat(couponDetail.discount) / 100
      return unitAmount * percent
    } else {
      return parseFloat(couponDetail.discount) * 100
    }
  }, [selectedPlan, couponDetail])

  // Pre-tax amount (dollars)
  const baseAmount = useMemo(() => {
    if (!selectedPlan) return 0
    return (selectedPlan.unitAmount - computeDiscount) / 100
  }, [selectedPlan, computeDiscount])

  // Final amount (dollars)
  const computeTotalAmount = useMemo(() => {
    if (!selectedPlan) return 0
    if (!taxExemption && taxDetail?.totalAmount) {
      return taxDetail.totalAmount
    }
    return baseAmount
  }, [selectedPlan, baseAmount, taxDetail, taxExemption])

  // Keep a stable ref to formData so getTaxEstimate doesn't need it as a dep
  const formDataRef = useRef(formData)
  formDataRef.current = formData

  // Tax recomputation — reads formData via ref to avoid recreating on every context change
  const getTaxEstimate = useCallback(async () => {
    if (!selectedPlan) return
    const fd = formDataRef.current
    try {
      const res = (
        await getTaxDetails({
          variables: {
            priceID: fd.stripe_price_id || selectedPlan.id,
            input: {
              address_source: fd.streetAddress,
              country: 'US',
              state: fd.state,
              currency: 'usd',
              postal_code: fd.zip,
              amount: baseAmount,
            },
          },
        })
      ).data

      if (res?.computeTax) {
        const { __typename, ...rest } = res.computeTax
        setTaxDetails(rest)
      }
    } catch (err) {
      console.error('Tax estimation failed:', err)
    }
  }, [selectedPlan, baseAmount, getTaxDetails])

  // Trigger recompute when plan, coupon, or exemption changes — not on every formData change
  useEffect(() => {
    if (selectedPlan) {
      getTaxEstimate()
    }
  }, [selectedPlan, couponDetail, taxExemption, getTaxEstimate])

  useEffect(() => {
    if (couponDetail?.duration_in_months >= 3 && selectedPlan) {
      const totalBalance = computeTotalAmount - (computeTotalAmount / 12) * 3
      setFormData((prev) => ({ ...prev, balance: totalBalance }))
    } else if (!couponDetail) {
      setFormData((prev) => ({ ...prev, balance: 0 }))
    }
  }, [computeTotalAmount, couponDetail, selectedPlan, setFormData])

  const handlePaymentType = async (paymentType: string) => {
    if (paymentType.toLowerCase() === 'card') {
      setCheckoutType('card')
      return
    }

    try {
      const res = (
        await getFinancialConnectionSession({
          variables: {
            input: {
              email: formData.contactEmail,
              full_name: `${formData.firstName} ${formData.lastName}`,
              tax_exemption: taxExemption,
              price_id: selectedPlan?.id,
              amount: selectedPlan?.unitAmount,
            },
          },
        })
      ).data

      if (res?.createFinancialConnectionsSession) {
        setCheckoutType('ach')
        setFinConnSession(res.createFinancialConnectionsSession)
        setPaymentState((prev) => ({ ...prev, paymentType: true }))
      }
    } catch (err) {
      console.error(err)
    }
  }

  // When payment succeeds, call onSuccess to show our success modal
  useEffect(() => {
    if (paymentState.success) {
      onSuccess(taxExemption)
    }
  }, [paymentState.success, onSuccess, taxExemption])

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <style jsx global>{`
        .scrollbar-tiny::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .scrollbar-tiny::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-tiny::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .scrollbar-tiny::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
      <DialogContent aria-describedby={undefined} className="max-w-[1000px] max-h-[90vh] overflow-hidden p-0 flex flex-col">
        <DialogTitle className="sr-only">Upgrade to Professional Plan</DialogTitle>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-gray-100 rounded-full p-2 text-neutral-500 hover:text-neutral-700 transition-colors z-50"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="overflow-y-auto scrollbar-tiny w-full h-full">
          <div className="flex xs:flex-col xxs:flex-col xxxs:flex-col justify-between w-full items-stretch">
            {/* Left Side - Plan Selection & Summary */}
            <div className="flex flex-col w-1/2 bg-[#F3FAEB] xs:w-full xxs:w-full xxxs:w-full xs:mb-3 xxs:mb-3 xxxs:mb-3 p-6">
              <div className="border-b border-gray-300 pb-6">
              <div className="flex items-center gap-2">
                <Image
                  src="/images/logo.svg"
                  alt="Culineer"
                  className="w-20 pointer-events-none"
                  width={80}
                  height={28}
                />
              </div>
            </div>

            <div className="border-b border-gray-300 pb-6">
              <div className="py-6 font-medium">
                <h1>Choose Plan</h1>
              </div>
              <div className="flex items-center gap-2 bg-white p-2 rounded-md">
                <div className="flex flex-col w-full">
                  {pricesLoading ? (
                    <div className="text-center py-4">Loading plans...</div>
                  ) : pricesError ? (
                    <div className="text-center py-4 text-red-500">Error loading plans: {pricesError.message}</div>
                  ) : !data?.stripePrices || data.stripePrices.length === 0 ? (
                    <div className="text-center py-4">No plans available</div>
                  ) : (
                    data.stripePrices
                      .slice()
                      .sort((a, b) => {
                        // Sort to show annual/year first, then monthly
                        if (a.interval?.toLowerCase() === 'year') return -1
                        if (b.interval?.toLowerCase() === 'year') return 1
                        return 0
                      })
                      .map((product, index) => (
                        <SubscriptionPlanCard
                          key={index}
                          product={product}
                          selectedPlan={selectedPlan}
                          setSelectedPlan={setSelectedPlan}
                          formData={formData}
                          setFormData={setFormData}
                        />
                      ))
                  )}
                </div>
              </div>
            </div>

            <div className="pb-3 mt-4">
              <h1 className="py-4 font-semibold">Summary</h1>
              <div className="pt-3">
                <h6 className="font-normal text-sm">Culineer Subscription</h6>
                <h1 className="py-2 font-semibold text-xl">
                  $
                  {Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(
                    selectedPlan?.unitAmount / 100 || 0
                  )}
                </h1>
              </div>
            </div>

            <div className="flex items-center justify-between w-full py-4">
              <div className="flex gap-3 items-center">
                <img src="/plan_logo.png" alt="plan logo" className="h-8 w-8" />
                <div>
                  <h1 className="font-medium">Professional Plan - {selectedPlan?.name}</h1>
                  <span className="text-sm text-gray-400">Cancel anytime</span>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <h1 className="font-semibold">
                  $
                  {Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(
                    selectedPlan?.unitAmount / 100 || 0
                  )}
                </h1>
                <span className="text-sm">{selectedPlan?.name} Plan</span>
              </div>
            </div>

            <div className="flex items-center justify-between pb-3 mt-4">
              <div className="w-1/2"></div>
              <div className="flex justify-between border-b border-gray-300 pb-3 w-1/2">
                <span className="font-medium">Subtotal</span>
                <h1>
                  $
                  {Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(selectedPlan?.unitAmount / 100 || 0)}
                </h1>
              </div>
            </div>

            {!taxExemption && (
              <div className="flex items-center justify-between">
                <div className="w-1/2"></div>
                <div className="flex justify-between border-b border-gray-300 pb-3 w-1/2">
                  <span>
                    Sales tax (
                    {Intl.NumberFormat('en-US', { minimumFractionDigits: 1 }).format(
                      taxDetail?.taxPercentage ?? 0
                    )}
                    %)
                  </span>
                  <h1>
                    $
                    {Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(taxDetail?.taxAmount ?? 0)}
                  </h1>
                </div>
              </div>
            )}

            {couponDetail?.discount && (
              <div className="flex items-center justify-between pt-3">
                <div className="w-1/2"></div>
                <div className="flex justify-between border-b border-gray-300 pb-3 w-1/2">
                  <span>
                    Discount (
                    {Intl.NumberFormat('en-US', { minimumFractionDigits: 1 }).format(
                      parseFloat(couponDetail?.discount.split('%')[0]) ?? 0
                    )}
                    %)
                  </span>
                  <h1>
                    $
                    {Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(computeDiscount / 100 || 0)}
                  </h1>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 pb-2">
              <div className="w-1/2"></div>
              <div className="flex justify-between w-1/2">
                <span className="font-medium">Total due</span>
                <h1>
                  $
                  {Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(computeTotalAmount || 0)}
                </h1>
              </div>
            </div>

            <div className="flex pt-10 items-center mt-auto">
              <div className="flex gap-1 items-center">
                <span className="text-sm font-normal">Powered by</span>
                <span className="font-medium">Stripe</span>
              </div>
              <div className="px-5 text-sm text-gray-400">|</div>
              <div className="flex gap-4 text-sm">
                <Link href={'/terms-of-service'} target="__blank">
                  Terms
                </Link>
                <Link href={'/terms-of-service'} target="__blank">
                  Privacy
                </Link>
              </div>
            </div>
          </div>

          {/* Right Side - Checkout Form */}
          <div className="w-1/2 xs:w-full xxs:w-full xxxs:w-full pl-8 pr-6 pt-16 pb-6 xxs:pl-6 xxxs:pl-6 xs:pl-6">
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-semibold text-xl">Checkout</h1>
              <div className="flex h-12 w-7/12 border border-[#6EAC36]/30 rounded-full items-center p-[1px]">
                <div
                  className={`flex w-2/3 h-full gap-3 rounded-full justify-center items-center cursor-pointer ${
                    checkoutType === 'card' ? 'bg-[#3978D7]' : 'bg-white'
                  }`}
                  onClick={() => handlePaymentType('card')}
                >
                  <Card
                    variant="Bold"
                    size={24}
                    color={`${checkoutType === 'card' ? '#fff' : '#4A4A4A'}`}
                  />
                  <h1
                    className={`text-sm text-center xs:hidden xxs:hidden xxxs:hidden ${
                      checkoutType === 'card' ? 'text-white' : ''
                    }`}
                  >
                    Credit/Debit Card
                  </h1>
                </div>
                <div
                  className={`flex w-1/3 h-full gap-2 rounded-full justify-center items-center cursor-pointer ${
                    checkoutType === 'ach' ? 'bg-[#3978D7]' : 'bg-white'
                  }`}
                  onClick={async () => handlePaymentType('ach')}
                >
                  <Bank
                    variant="Bold"
                    size={24}
                    color={`${checkoutType === 'ach' ? '#fff' : '#4A4A4A'}`}
                  />
                  <h1
                    className={`text-sm xs:hidden xxs:hidden xxxs:hidden ${
                      checkoutType === 'ach' ? 'text-white' : ''
                    }`}
                  >
                    ACH
                  </h1>
                </div>
              </div>
            </div>

            <Elements stripe={getStripe()} key="stripe-elements">
              <StripeCheckoutForm
                totalAmount={computeTotalAmount}
                setTaxExemption={setTaxExemption}
                checkoutType={checkoutType}
                planId={selectedPlan?.id}
                priceId={selectedPlan?.id}
                finConnSession={finConnSession}
                setFinConnSession={setFinConnSession}
                setPaymentState={setPaymentState}
                setCouponDetail={setCouponDetail}
              />
            </Elements>
          </div>
        </div>
      </div>
    </DialogContent>
    </Dialog>
  )
}
